import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import { Schedule, ScheduleCreate, ScheduleUpdate, SchedulePreviewRun } from '../../types';

interface ScheduleConfigPanelProps {
  workflowId: number;
  onScheduleUpdate?: (schedule: Schedule | null) => void;
}

type ScheduleType = 'interval' | 'cron' | 'one_time';
type CronPreset = 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'hourly' | 'custom';

interface FormState {
  is_enabled: boolean;
  schedule_type: ScheduleType;
  interval_minutes: string;
  next_run_at: string;
  // Cron fields
  cron_preset: CronPreset;
  cron_time: string;
  cron_day_of_week: string;
  cron_day_of_month: string;
  cron_minute: string;
  cron_expression: string;
  timezone: string;
}

const DAY_OF_WEEK_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => ({
  value: String(i),
  label: `:${String(i).padStart(2, '0')}`,
}));

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

/** Build a cron expression from the preset and form state */
function buildCronExpression(preset: CronPreset, form: FormState): string {
  const [hours, minutes] = form.cron_time.split(':').map(Number);
  switch (preset) {
    case 'daily':    return `${minutes} ${hours} * * *`;
    case 'weekdays': return `${minutes} ${hours} * * 1-5`;
    case 'weekly':   return `${minutes} ${hours} * * ${form.cron_day_of_week}`;
    case 'monthly':  return `${minutes} ${hours} ${form.cron_day_of_month} * *`;
    case 'hourly':   return `${parseInt(form.cron_minute)} * * * *`;
    case 'custom':   return form.cron_expression;
  }
}

/** Parse a cron expression back into a preset and form fields */
function parseCronExpression(expr: string): Partial<FormState> {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return { cron_preset: 'custom', cron_expression: expr };

  const [minute, hour, dom, , dow] = parts;

  // Hourly: M * * * *
  if (hour === '*' && dom === '*' && dow === '*') {
    return { cron_preset: 'hourly', cron_minute: minute };
  }

  const time = `${String(parseInt(hour)).padStart(2, '0')}:${String(parseInt(minute)).padStart(2, '0')}`;

  // Daily: M H * * *
  if (dom === '*' && dow === '*') {
    return { cron_preset: 'daily', cron_time: time };
  }

  // Weekdays: M H * * 1-5
  if (dom === '*' && dow === '1-5') {
    return { cron_preset: 'weekdays', cron_time: time };
  }

  // Weekly: M H * * D
  if (dom === '*' && /^[0-6]$/.test(dow)) {
    return { cron_preset: 'weekly', cron_time: time, cron_day_of_week: dow };
  }

  // Monthly: M H D * *
  if (dow === '*' && /^\d{1,2}$/.test(dom)) {
    return { cron_preset: 'monthly', cron_time: time, cron_day_of_month: dom };
  }

  return { cron_preset: 'custom', cron_expression: expr };
}

/** Get a human-readable description of a cron schedule */
function describeCronSchedule(expr: string, tz: string | null): string {
  const parsed = parseCronExpression(expr);
  const tzLabel = tz || 'UTC';
  const time = parsed.cron_time || '';

  const formatTime = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  switch (parsed.cron_preset) {
    case 'daily': return `Every day at ${formatTime(time)} (${tzLabel})`;
    case 'weekdays': return `Weekdays at ${formatTime(time)} (${tzLabel})`;
    case 'weekly': {
      const day = DAY_OF_WEEK_OPTIONS.find(d => d.value === parsed.cron_day_of_week)?.label || '';
      return `Every ${day} at ${formatTime(time)} (${tzLabel})`;
    }
    case 'monthly': return `Monthly on day ${parsed.cron_day_of_month} at ${formatTime(time)} (${tzLabel})`;
    case 'hourly': return `Every hour at :${String(parseInt(parsed.cron_minute || '0')).padStart(2, '0')} (${tzLabel})`;
    default: return `${expr} (${tzLabel})`;
  }
}

/** Get common timezones, with browser default first */
function getTimezoneOptions(): { value: string; label: string }[] {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let allZones: string[];
  try {
    allZones = (Intl as any).supportedValuesOf('timeZone');
  } catch {
    // Fallback for older browsers
    allZones = [
      'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
      'America/Toronto', 'America/Vancouver', 'Europe/London', 'Europe/Berlin', 'Europe/Paris',
      'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Australia/Sydney', 'Pacific/Auckland',
    ];
  }
  // Put browser timezone first if not already
  const sorted = allZones.filter(z => z !== browserTz);
  sorted.unshift(browserTz);
  return sorted.map(z => ({ value: z, label: z.replace(/_/g, ' ') }));
}

const TIMEZONE_OPTIONS = getTimezoneOptions();

const getDefaultFormState = (): FormState => {
  const date = new Date();
  date.setHours(date.getHours() + 1);
  date.setMinutes(0, 0, 0);
  return {
    is_enabled: true,
    schedule_type: 'interval',
    interval_minutes: '60',
    next_run_at: date.toISOString().slice(0, 16),
    cron_preset: 'daily',
    cron_time: '09:00',
    cron_day_of_week: '1',
    cron_day_of_month: '1',
    cron_minute: '0',
    cron_expression: '0 9 * * *',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

const ScheduleConfigPanel: React.FC<ScheduleConfigPanelProps> = ({
  workflowId,
  onScheduleUpdate
}) => {
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formState, setFormState] = useState<FormState>(getDefaultFormState());
  const [originalFormState, setOriginalFormState] = useState<FormState>(getDefaultFormState());
  const [validationError, setValidationError] = useState<string | null>(null);
  const [previewRuns, setPreviewRuns] = useState<SchedulePreviewRun[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Validate interval minutes
  const validateIntervalMinutes = useCallback((value: string): string | null => {
    if (!value || value.trim() === '') return 'Interval is required';
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue <= 0) return 'Interval must be greater than 0';
    return null;
  }, []);

  // Update validation when form changes
  useEffect(() => {
    if (formState.schedule_type === 'interval') {
      setValidationError(validateIntervalMinutes(formState.interval_minutes));
    } else if (formState.schedule_type === 'cron') {
      if (formState.cron_preset === 'custom' && !formState.cron_expression.trim()) {
        setValidationError('Cron expression is required');
      } else {
        setValidationError(null);
      }
    } else {
      setValidationError(null);
    }
  }, [formState.interval_minutes, formState.schedule_type, formState.cron_preset, formState.cron_expression, validateIntervalMinutes]);

  // Fetch preview runs when cron config changes (debounced)
  useEffect(() => {
    if (formState.schedule_type !== 'cron') {
      setPreviewRuns([]);
      return;
    }

    const expr = buildCronExpression(formState.cron_preset, formState);
    if (!expr.trim()) {
      setPreviewRuns([]);
      return;
    }

    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);

    previewTimeoutRef.current = setTimeout(async () => {
      setIsLoadingPreview(true);
      try {
        const runs = await apiService.previewNextRuns(expr, formState.timezone, 3);
        setPreviewRuns(runs);
      } catch {
        setPreviewRuns([]);
      } finally {
        setIsLoadingPreview(false);
      }
    }, 500);

    return () => {
      if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    };
  }, [formState.schedule_type, formState.cron_preset, formState.cron_time, formState.cron_day_of_week, formState.cron_day_of_month, formState.cron_minute, formState.cron_expression, formState.timezone]);

  // Initialize form from schedule data
  const initFormFromSchedule = useCallback((data: Schedule): FormState => {
    const base = getDefaultFormState();
    const form: FormState = {
      ...base,
      is_enabled: data.is_enabled,
      schedule_type: data.schedule_type,
      interval_minutes: data.interval_minutes?.toString() || '60',
      next_run_at: data.next_run_at ? new Date(data.next_run_at).toISOString().slice(0, 16) : base.next_run_at,
      timezone: data.timezone || base.timezone,
    };
    if (data.schedule_type === 'cron' && data.cron_expression) {
      const parsed = parseCronExpression(data.cron_expression);
      Object.assign(form, parsed);
      form.cron_expression = data.cron_expression;
    }
    return form;
  }, []);

  // Fetch schedule
  const fetchSchedule = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      // getScheduleByWorkflow returns null when no schedule exists (404)
      const data = await apiService.getScheduleByWorkflow(workflowId);
      setSchedule(data);
      if (data) {
        const newFormState = initFormFromSchedule(data);
        setFormState(newFormState);
        setOriginalFormState(newFormState);
      }
      setShowCreateForm(false);
    } catch (err: any) {
      setError('Failed to load schedule');
    } finally {
      setIsLoading(false);
    }
  }, [workflowId, initFormFromSchedule]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  // Handlers
  const handleCancel = () => {
    if (schedule) {
      setFormState(originalFormState);
      setValidationError(null);
    } else {
      setShowCreateForm(false);
      setFormState(getDefaultFormState());
      setValidationError(null);
    }
    setError(null);
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    if (formState.schedule_type === 'interval') {
      const intervalError = validateIntervalMinutes(formState.interval_minutes);
      if (intervalError) { setValidationError(intervalError); return; }
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (schedule) {
        const updateData: ScheduleUpdate = {
          is_enabled: formState.is_enabled,
          schedule_type: formState.schedule_type,
        };
        if (formState.schedule_type === 'interval') {
          updateData.interval_minutes = parseInt(formState.interval_minutes, 10);
        }
        if (formState.schedule_type === 'cron') {
          updateData.cron_expression = buildCronExpression(formState.cron_preset, formState);
          updateData.timezone = formState.timezone;
        }
        const updated = await apiService.updateSchedule(schedule.schedule_id, updateData);
        setSchedule(updated);
        const newForm = initFormFromSchedule(updated);
        setOriginalFormState(newForm);
        setSuccessMessage('Schedule saved successfully');
        onScheduleUpdate?.(updated);
      } else {
        const createData: ScheduleCreate = {
          orchestration_id: workflowId,
          schedule_type: formState.schedule_type,
          is_enabled: formState.is_enabled,
        };
        if (formState.schedule_type === 'interval') {
          createData.interval_minutes = parseInt(formState.interval_minutes, 10);
        }
        if (formState.schedule_type === 'cron') {
          createData.cron_expression = buildCronExpression(formState.cron_preset, formState);
          createData.timezone = formState.timezone;
        }
        const newSchedule = await apiService.createSchedule(createData);
        setSchedule(newSchedule);
        const newForm = initFormFromSchedule(newSchedule);
        setFormState(newForm);
        setOriginalFormState(newForm);
        setShowCreateForm(false);
        onScheduleUpdate?.(newSchedule);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Failed to save schedule');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!schedule) return;
    setIsDeleting(true);
    setError(null);
    try {
      await apiService.deleteSchedule(schedule.schedule_id);
      setSchedule(null);
      setShowDeleteConfirm(false);
      setShowCreateForm(false);
      setFormState(getDefaultFormState());
      onScheduleUpdate?.(null);
    } catch {
      setError('Failed to delete schedule');
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try { return new Date(dateString).toLocaleString(); } catch { return dateString; }
  };

  const isFormValid = (() => {
    if (validationError) return false;
    if (formState.schedule_type === 'interval') return formState.interval_minutes.trim() !== '';
    if (formState.schedule_type === 'cron') {
      const expr = buildCronExpression(formState.cron_preset, formState);
      return expr.trim() !== '';
    }
    return false;
  })();

  // Loading
  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-gray-300 dark:border-gray-600 border-t-blue-600 rounded-full animate-spin mr-2" />
          <span className="text-gray-600 dark:text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  // Error
  if (error && !schedule && !showCreateForm) {
    return (
      <div className="p-4">
        <div className="text-red-600 mb-4">{error}</div>
        <button onClick={fetchSchedule} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">Retry</button>
      </div>
    );
  }

  // No schedule
  if (!schedule && !showCreateForm) {
    return (
      <div className="p-4">
        <p className="text-gray-600 dark:text-gray-400 mb-4">No schedule configured</p>
        <button onClick={() => setShowCreateForm(true)} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">Create Schedule</button>
      </div>
    );
  }

  // Main form
  return (
    <div className="p-4">
      {successMessage && <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg">{successMessage}</div>}
      {error && <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">{error}</div>}

      {/* Enabled toggle */}
      <div className="mb-4">
        <label className="flex items-center cursor-pointer">
          <input type="checkbox" checked={formState.is_enabled} onChange={e => setFormState(prev => ({ ...prev, is_enabled: e.target.checked }))} aria-label="Enabled" className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500" />
          <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Enabled</span>
        </label>
      </div>

      {/* Schedule type */}
      <div className="mb-4" data-tutorial="schedule-type-selector">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schedule Type</label>
        <CustomSelect
          value={formState.schedule_type}
          onChange={v => setFormState(prev => ({ ...prev, schedule_type: v as ScheduleType }))}
          options={[
            { value: 'interval', label: 'Interval (every N minutes)' },
            { value: 'cron', label: 'Scheduled (specific times)' },
          ]}
          aria-label="Schedule Type"
        />
      </div>

      {/* Interval config */}
      {formState.schedule_type === 'interval' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Run Every (minutes)</label>
          <input
            type="number"
            value={formState.interval_minutes}
            onChange={e => setFormState(prev => ({ ...prev, interval_minutes: e.target.value }))}
            aria-label="Run Every (minutes)"
            min="1"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-warm-white ${validationError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
          />
          {validationError && <p className="mt-1 text-sm text-red-600">{validationError}</p>}
        </div>
      )}

      {/* Cron config */}
      {formState.schedule_type === 'cron' && (
        <div className="mb-4 space-y-4">
          {/* Frequency preset */}
          <div data-tutorial="schedule-cron-preset">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Frequency</label>
            <CustomSelect
              value={formState.cron_preset}
              onChange={v => setFormState(prev => ({ ...prev, cron_preset: v as CronPreset }))}
              options={[
                { value: 'daily', label: 'Every day' },
                { value: 'weekdays', label: 'Weekdays (Mon-Fri)' },
                { value: 'weekly', label: 'Weekly on a specific day' },
                { value: 'monthly', label: 'Monthly on a specific day' },
                { value: 'hourly', label: 'Every hour' },
                { value: 'custom', label: 'Custom cron expression' },
              ]}
              aria-label="Frequency"
            />
          </div>

          {/* Time picker (for daily, weekdays, weekly, monthly) */}
          {['daily', 'weekdays', 'weekly', 'monthly'].includes(formState.cron_preset) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time</label>
              <input
                type="time"
                value={formState.cron_time}
                onChange={e => setFormState(prev => ({ ...prev, cron_time: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-warm-white"
                data-tutorial="schedule-time-picker"
              />
            </div>
          )}

          {/* Day of week (weekly) */}
          {formState.cron_preset === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Day of Week</label>
              <CustomSelect
                value={formState.cron_day_of_week}
                onChange={v => setFormState(prev => ({ ...prev, cron_day_of_week: v }))}
                options={DAY_OF_WEEK_OPTIONS}
                aria-label="Day of Week"
              />
            </div>
          )}

          {/* Day of month (monthly) */}
          {formState.cron_preset === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Day of Month</label>
              <CustomSelect
                value={formState.cron_day_of_month}
                onChange={v => setFormState(prev => ({ ...prev, cron_day_of_month: v }))}
                options={DAY_OF_MONTH_OPTIONS}
                aria-label="Day of Month"
              />
            </div>
          )}

          {/* Minute (hourly) */}
          {formState.cron_preset === 'hourly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Minute of Hour</label>
              <CustomSelect
                value={formState.cron_minute}
                onChange={v => setFormState(prev => ({ ...prev, cron_minute: v }))}
                options={MINUTE_OPTIONS}
                aria-label="Minute of Hour"
              />
            </div>
          )}

          {/* Custom cron expression */}
          {formState.cron_preset === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cron Expression</label>
              <input
                type="text"
                value={formState.cron_expression}
                onChange={e => setFormState(prev => ({ ...prev, cron_expression: e.target.value }))}
                placeholder="0 9 * * *  (min hour dom month dow)"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-warm-white font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Format: minute hour day-of-month month day-of-week
              </p>
            </div>
          )}

          {/* Timezone */}
          <div data-tutorial="schedule-timezone">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Timezone</label>
            <CustomSelect
              value={formState.timezone}
              onChange={v => setFormState(prev => ({ ...prev, timezone: v }))}
              options={TIMEZONE_OPTIONS}
              aria-label="Timezone"
            />
          </div>

          {/* Preview next runs */}
          <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Next 3 scheduled runs</p>
            {isLoadingPreview ? (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <div className="w-3 h-3 border border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                Calculating...
              </div>
            ) : previewRuns.length > 0 ? (
              <ul className="space-y-1">
                {previewRuns.map((run, i) => (
                  <li key={i} className="text-sm text-gray-700 dark:text-gray-300">
                    {new Date(run.local).toLocaleString(undefined, {
                      weekday: 'short', month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
                    })}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-gray-400">Enter a valid schedule to see preview</p>
            )}
          </div>

          {validationError && <p className="text-sm text-red-600">{validationError}</p>}
        </div>
      )}

      {/* One-time (still coming soon) */}
      {formState.schedule_type === 'one_time' && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            <strong>Coming Soon:</strong> One-time scheduling is planned for a future release.
          </p>
        </div>
      )}

      {/* Schedule status (existing schedule) */}
      {schedule && (
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
          {schedule.schedule_type === 'cron' && schedule.cron_expression && (
            <div className="mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Schedule: </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">{describeCronSchedule(schedule.cron_expression, schedule.timezone)}</span>
            </div>
          )}
          <div className="mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Next Run: </span>
            {formState.is_enabled ? (
              <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(schedule.next_run_at)}</span>
            ) : (
              <span className="text-sm text-gray-400 italic">Paused</span>
            )}
          </div>
          <div className="mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Last Run: </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(schedule.last_run_at)}</span>
          </div>
          {schedule.last_run_status && (
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status: </span>
              <span className={`text-sm ${schedule.last_run_status === 'completed' ? 'text-green-600' : schedule.last_run_status === 'failed' ? 'text-red-600' : 'text-yellow-600'}`}>
                {schedule.last_run_status}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      {!showDeleteConfirm && (
        <div className="flex items-center space-x-3">
          <button onClick={handleSave} disabled={!isFormValid || isSaving} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed" data-tutorial="schedule-save-btn">
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={handleCancel} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
            Cancel
          </button>
          {schedule && (
            <button onClick={() => setShowDeleteConfirm(true)} disabled={isSaving || isDeleting} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
              Delete
            </button>
          )}
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-2">Confirm Deletion</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">Are you sure you want to delete this schedule? This action cannot be undone.</p>
            <div className="flex items-center justify-end space-x-3">
              <button onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">Cancel</button>
              <button onClick={handleDelete} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">{isDeleting ? 'Deleting...' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleConfigPanel;
